/**
 * useMessaging Hook
 * Comprehensive messaging hook for conversation management
 * Based on ImpactIQ v2 architecture patterns
 *
 * Features:
 * - Conversation list with unread counts
 * - Find or create direct message conversations
 * - Cursor-based pagination
 * - Optimistic updates with rollback
 * - Real-time unread count tracking
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { messagesAPI, APIError } from '../services/api';

export interface Conversation {
  id: string;
  partner_id: string;
  partner_name: string;
  partner_avatar: string;
  partner_email?: string;
  last_message: string;
  last_message_at: string;
  unread_count: number;
  is_online?: boolean;
}

export interface Message {
  id: string;
  conversation_id?: string;
  from_user_id: string;
  to_user_id: string;
  content: string;
  read: boolean;
  created_at: string;
  updated_at?: string;
  sender_name?: string;
  sender_avatar?: string;
  sender_role?: 'admin' | 'subscriber';
  status?: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
  attachments?: string[];
  edited?: boolean;
}

export interface UseMessagingReturn {
  // Conversations
  conversations: Conversation[];
  conversationsLoading: boolean;
  conversationsError: string | null;
  refreshConversations: () => Promise<void>;
  findOrCreateDM: (userId: string) => Promise<string | null>;

  // Selected conversation
  selectedConversation: Conversation | null;
  selectConversation: (conversationId: string | null) => void;

  // Messages
  messages: Message[];
  messagesLoading: boolean;
  messagesError: string | null;
  hasMoreMessages: boolean;
  loadingMoreMessages: boolean;
  loadMessages: (conversationId: string) => Promise<void>;
  loadMoreMessages: () => Promise<void>;

  // Actions
  sendMessage: (content: string, attachments?: string[]) => Promise<boolean>;
  editMessage: (messageId: string, content: string) => Promise<boolean>;
  deleteMessage: (messageId: string) => Promise<boolean>;
  markAsRead: (conversationId: string) => Promise<void>;

  // Stats
  totalUnreadCount: number;
  isSending: boolean;
}

export function useMessaging(): UseMessagingReturn {
  // Conversations state
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [conversationsError, setConversationsError] = useState<string | null>(null);

  // Selected conversation
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);

  // Messages state
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);

  // Sending state
  const [isSending, setIsSending] = useState(false);
  const sendLockRef = useRef(false);

  // Cursor for pagination
  const oldestMessageIdRef = useRef<string | null>(null);

  // Load conversations
  const refreshConversations = useCallback(async () => {
    setConversationsLoading(true);
    setConversationsError(null);

    try {
      const data = await messagesAPI.getConversations();
      setConversations(data.conversations || []);
    } catch (err) {
      const message = err instanceof APIError ? err.message : 'Failed to load conversations';
      setConversationsError(message);
    } finally {
      setConversationsLoading(false);
    }
  }, []);

  // Find or create a DM conversation
  const findOrCreateDM = useCallback(async (userId: string): Promise<string | null> => {
    try {
      const data = await messagesAPI.findOrCreateDM(userId);
      // Refresh conversations to include the new one
      await refreshConversations();
      return data.conversationId || data.id;
    } catch (err) {
      const message = err instanceof APIError ? err.message : 'Failed to create conversation';
      setConversationsError(message);
      return null;
    }
  }, [refreshConversations]);

  // Select a conversation
  const selectConversation = useCallback((conversationId: string | null) => {
    if (!conversationId) {
      setSelectedConversation(null);
      setMessages([]);
      return;
    }

    const conversation = conversations.find(c => c.id === conversationId || c.partner_id === conversationId);
    setSelectedConversation(conversation || null);
  }, [conversations]);

  // Load messages for a conversation (cursor-based)
  const loadMessages = useCallback(async (conversationId: string) => {
    setMessagesLoading(true);
    setMessagesError(null);
    setMessages([]);
    oldestMessageIdRef.current = null;

    try {
      const data = await messagesAPI.getMessages(conversationId);
      const loadedMessages = data.messages || [];
      setMessages(loadedMessages);
      setHasMoreMessages(loadedMessages.length === 50);

      if (loadedMessages.length > 0) {
        oldestMessageIdRef.current = loadedMessages[0].id;
      }
    } catch (err) {
      const message = err instanceof APIError ? err.message : 'Failed to load messages';
      setMessagesError(message);
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  // Load more messages (pagination)
  const loadMoreMessages = useCallback(async () => {
    if (!selectedConversation || !hasMoreMessages || loadingMoreMessages) return;
    if (!oldestMessageIdRef.current) return;

    setLoadingMoreMessages(true);

    try {
      const conversationId = selectedConversation.id || selectedConversation.partner_id;
      const data = await messagesAPI.getMessages(conversationId, oldestMessageIdRef.current);
      const loadedMessages = data.messages || [];

      if (loadedMessages.length > 0) {
        setMessages(prev => [...loadedMessages, ...prev]);
        oldestMessageIdRef.current = loadedMessages[0].id;
      }

      setHasMoreMessages(loadedMessages.length === 50);
    } catch (err) {
      const message = err instanceof APIError ? err.message : 'Failed to load more messages';
      setMessagesError(message);
    } finally {
      setLoadingMoreMessages(false);
    }
  }, [selectedConversation, hasMoreMessages, loadingMoreMessages]);

  // Send a message with optimistic update
  const sendMessage = useCallback(async (content: string, attachments?: string[]): Promise<boolean> => {
    if (!selectedConversation) return false;
    if (sendLockRef.current || (!content.trim() && (!attachments || attachments.length === 0))) {
      return false;
    }

    sendLockRef.current = true;
    setIsSending(true);

    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const conversationId = selectedConversation.id || selectedConversation.partner_id;

    // Optimistic update
    const optimisticMessage: Message = {
      id: tempId,
      conversation_id: conversationId,
      from_user_id: 'self',
      to_user_id: selectedConversation.partner_id,
      content: content.trim(),
      read: false,
      created_at: new Date().toISOString(),
      status: 'pending',
      attachments,
    };

    setMessages(prev => [...prev, optimisticMessage]);

    try {
      const data = await messagesAPI.sendMessage(conversationId, content.trim(), attachments);

      // Replace optimistic message with real one
      setMessages(prev => prev.map(m =>
        m.id === tempId
          ? { ...m, id: data.id || data.messageId, status: 'sent' as const }
          : m
      ));

      // Update conversation list
      setConversations(prev => prev.map(c =>
        (c.id === conversationId || c.partner_id === conversationId)
          ? { ...c, last_message: content.trim(), last_message_at: new Date().toISOString() }
          : c
      ));

      return true;
    } catch (err) {
      // Rollback optimistic update
      setMessages(prev => prev.filter(m => m.id !== tempId));
      const message = err instanceof APIError ? err.message : 'Failed to send message';
      setMessagesError(message);
      return false;
    } finally {
      setIsSending(false);
      sendLockRef.current = false;
    }
  }, [selectedConversation]);

  // Edit a message
  const editMessage = useCallback(async (messageId: string, content: string): Promise<boolean> => {
    if (!content.trim()) return false;

    // Find original message for rollback
    const originalMessage = messages.find(m => m.id === messageId);
    if (!originalMessage) return false;

    // Optimistic update
    setMessages(prev => prev.map(m =>
      m.id === messageId
        ? { ...m, content: content.trim(), edited: true, updated_at: new Date().toISOString() }
        : m
    ));

    try {
      await messagesAPI.editMessage(messageId, content.trim());
      return true;
    } catch (err) {
      // Rollback
      setMessages(prev => prev.map(m =>
        m.id === messageId ? originalMessage : m
      ));
      const message = err instanceof APIError ? err.message : 'Failed to edit message';
      setMessagesError(message);
      return false;
    }
  }, [messages]);

  // Delete a message
  const deleteMessage = useCallback(async (messageId: string): Promise<boolean> => {
    // Store for rollback
    const originalMessages = [...messages];

    // Optimistic update
    setMessages(prev => prev.filter(m => m.id !== messageId));

    try {
      await messagesAPI.deleteMessage(messageId);
      return true;
    } catch (err) {
      // Rollback
      setMessages(originalMessages);
      const message = err instanceof APIError ? err.message : 'Failed to delete message';
      setMessagesError(message);
      return false;
    }
  }, [messages]);

  // Mark conversation as read
  const markAsRead = useCallback(async (conversationId: string) => {
    try {
      await messagesAPI.markAsRead(conversationId);

      // Update local state
      setConversations(prev => prev.map(c =>
        (c.id === conversationId || c.partner_id === conversationId)
          ? { ...c, unread_count: 0 }
          : c
      ));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  }, []);

  // Calculate total unread count
  const totalUnreadCount = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  // Initial load
  useEffect(() => {
    refreshConversations();
  }, [refreshConversations]);

  // Load messages when conversation changes
  useEffect(() => {
    if (selectedConversation) {
      const conversationId = selectedConversation.id || selectedConversation.partner_id;
      loadMessages(conversationId);
      markAsRead(conversationId);
    }
  }, [selectedConversation, loadMessages, markAsRead]);

  return {
    // Conversations
    conversations,
    conversationsLoading,
    conversationsError,
    refreshConversations,
    findOrCreateDM,

    // Selected conversation
    selectedConversation,
    selectConversation,

    // Messages
    messages,
    messagesLoading,
    messagesError,
    hasMoreMessages,
    loadingMoreMessages,
    loadMessages,
    loadMoreMessages,

    // Actions
    sendMessage,
    editMessage,
    deleteMessage,
    markAsRead,

    // Stats
    totalUnreadCount,
    isSending,
  };
}

export default useMessaging;
